import type { LinkedTaskSummaryItem } from '@/lib/api/generated/model';

type ChildStatus = LinkedTaskSummaryItem['status'];

/**
 * What needs someone first (spec 130 FR-008). A task waiting to be started
 * needs someone too (spec 140 US2), after the ones already asking.
 */
const PRIORITY: Record<ChildStatus, number> = {
  needs_attention: 0,
  needs_review: 1,
  not_started: 2,
  in_progress: 3,
  done: 4,
};

/** The words for a child's state, wherever the children are listed. */
export const CHILD_STATUS_LABEL: Record<ChildStatus, string> = {
  needs_attention: 'Needs attention',
  needs_review: 'Needs review',
  not_started: 'Not started',
  in_progress: 'In progress',
  done: 'Done',
};

/** The dot beside a child's row, in the colours of the card's segmented bar (spec 130). */
export const CHILD_STATUS_DOT: Record<ChildStatus, string> = {
  needs_attention: 'bg-board-state-failed',
  needs_review: 'bg-board-state-waiting',
  // Hollow, as on the card's bar (spec 140 US1): nothing has happened yet.
  not_started: 'border border-dashed border-board-state-waiting',
  in_progress: 'bg-board-state-running',
  done: 'bg-board-state-done',
};

/**
 * The children of a linked-workflow join, in the order a reader should meet
 * them: the ones needing attention first, then review, then work in progress,
 * then what is done; alphabetical within each.
 */
export function orderLinkedChildren(
  children: readonly LinkedTaskSummaryItem[],
): LinkedTaskSummaryItem[] {
  return [...children].sort(
    (a, b) =>
      PRIORITY[a.status] - PRIORITY[b.status] || a.title.localeCompare(b.title),
  );
}

/**
 * How many children wait for someone to start them (spec 140 US1): the
 * service's count, or — on a summary from before it sent one — the list's.
 */
export function notStartedCount(
  linked:
    | {
        not_started?: number;
        tasks?: readonly LinkedTaskSummaryItem[];
      }
    | null
    | undefined,
): number {
  if (!linked) return 0;
  return (
    linked.not_started ??
    (linked.tasks ?? []).filter((child) => child.status === 'not_started')
      .length
  );
}

/** How many children need attention. */
export function needsAttentionCount(
  children: readonly LinkedTaskSummaryItem[] | undefined,
): number {
  return (children ?? []).filter((child) => child.status === 'needs_attention')
    .length;
}
